import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Video, Code2, ExternalLink } from 'lucide-react';

const NavigationBar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: 'https://viviacademy.online/', isSpecial: false },
    { name: 'Courses', path: 'https://viviacademy.online/courses', isSpecial: false },
    { 
      name: 'Meet', 
      path: 'https://meet.viviacademy.online', 
      isSpecial: true,
      isExternal: true,
      icon: Video,
      gradient: 'from-blue-500/80 to-purple-500/80',
      borderGradient: 'from-blue-400 to-purple-400'
    },
    { 
      name: 'Compiler', 
      path: 'https://compiler.viviacademy.online', 
      isSpecial: true,
      isExternal: true,
      icon: Code2,
      gradient: 'from-purple-500/80 to-pink-500/80',
      borderGradient: 'from-purple-400 to-pink-400'
    },
    { name: 'About', path: 'https://viviacademy.online/about', isSpecial: false },
    { name: 'Blog', path: 'https://viviacademy.online/blog', isSpecial: false },
    { name: 'Contact', path: 'https://viviacademy.online/contact', isSpecial: false },
  ];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when location changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Helper function to render nav links
  const renderNavLink = (link, isMobile = false) => {
    const isActive = !link.isExternal && location.pathname === link.path;
    const IconComponent = link.icon;
    
    // Special styling for Meet and Compiler buttons
    if (link.isSpecial) {
      const baseSpecialClasses = isMobile
        ? `relative block px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-500 group overflow-hidden border-2 bg-gradient-to-r ${link.gradient} hover:scale-105 transform animate-pulse`
        : `relative px-4 py-2 text-sm font-semibold transition-all duration-500 rounded-lg group overflow-hidden border-2 bg-gradient-to-r ${link.gradient} hover:scale-105 transform animate-pulse`;
  
      const linkContent = (
        <>
          {/* Subtle glow effect */}
          <div className={`absolute inset-0 bg-gradient-to-r ${link.borderGradient} opacity-20 blur-sm animate-pulse`} />
          
          {/* Shimmer effect */}
          <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 ease-out" />
          
          <div className="relative z-10 flex items-center space-x-1.5">
            {IconComponent && (
              <IconComponent className={`${isMobile ? 'h-4 w-4' : 'h-3.5 w-3.5'} animate-pulse`} />
            )}
            <span className="text-white font-medium">
              {link.name}
            </span>
            {/* REMOVED: ExternalLink icon that was causing duplication */}
          </div>
        </>
      );
  
      return (
        <a
          key={link.path}
          href={link.path}
          target="_blank"
          rel="noopener noreferrer"
          className={baseSpecialClasses}
        >
          {linkContent}
        </a>
      );
    }

    // Regular link styling
    const baseClasses = isMobile
    ? `block px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 transform hover:translate-x-2 ${
        isActive
          ? 'text-blue-400 bg-blue-500/10 border-l-4 border-blue-400'
          : 'text-gray-300 hover:text-white hover:bg-white/5'
      }`
    : `relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg group ${
        isActive
          ? 'text-blue-400'
          : 'text-gray-300 hover:text-white'
      }`;

  const linkContent = (
    <>
      {/* SIMPLIFIED: Single span for mobile, enhanced for desktop */}
      {isMobile ? (
        <div className="flex items-center justify-between">
          <span>{link.name}</span>
          {isActive && (
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
          )}
        </div>
      ) : (
        <>
          <span className="relative z-10">{link.name}</span>
          
          {/* Desktop-only hover effects */}
          <div className="absolute inset-0 bg-white/5 rounded-lg scale-0 group-hover:scale-100 transition-transform duration-300 ease-out" />
          
          {isActive && (
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg animate-pulse" />
          )}
          
          <div className={`absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-500 transition-all duration-300 ${
            isActive ? 'w-6' : 'group-hover:w-4'
          }`} />
        </>
      )}
    </>
  );

  return (
    <Link
      key={link.path}
      to={link.path}
      className={baseClasses}
    >
      {linkContent}
    </Link>
  );
};

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
          isScrolled 
            ? 'bg-[#121724]/95 backdrop-blur-xl border-b border-white/10 py-3' 
            : 'bg-[#121724]/80 backdrop-blur-sm py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            {/* Logo with micro-interaction */}
            <Link 
              to="/" 
              className="flex items-center group transition-all duration-300 hover:scale-105"
            >
              <div className="relative">
                <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400 bg-clip-text text-transparent">
                  ViviAcademy
                </span>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-500 group-hover:w-full transition-all duration-500 ease-out" />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link) => renderNavLink(link, false))}
            </nav>

            {/* User Actions */}
            <div className="flex items-center space-x-4">
              {/* Enhanced Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden group relative p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-300 hover:scale-110 border border-white/10 hover:border-white/20"
                aria-label="Toggle menu"
              >
                <div className="relative">
                  {isMenuOpen ? (
                    <X className="h-5 w-5 text-gray-300 group-hover:text-white transition-all duration-300 rotate-90" />
                  ) : (
                    <Menu className="h-5 w-5 text-gray-300 group-hover:text-white transition-all duration-300" />
                  )}
                </div>
                
                {/* Glow effect */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm" />
              </button>
            </div>
          </div>

          {/* Enhanced Mobile Navigation */}
          {isMenuOpen && (
  <nav className="lg:hidden mt-6 pb-4 border-t border-white/10 pt-6 animate-in slide-in-from-top-4 duration-500">
    <div className="space-y-2">
      {navLinks.map((link, index) => (
        <div
          key={`mobile-${link.path}-${index}`} // FIXED: More unique key
          className="animate-in slide-in-from-left duration-300"
          style={{
            animationDelay: `${index * 50}ms`
          }}
        >
          {renderNavLink(link, true)}
        </div>
      ))}
    </div>
  </nav>
)}
        </div>
      </header>

      {/* Background overlay for mobile menu */}
      {isMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
    </>
  );
};

export default NavigationBar;